import React from "react";
import moment from "moment";
import Icon from "@dtci/spellbook/Icon";
import styles from "./pptDiscountAuditTimeline.module.scss";

/**
 * Vertical timeline of audit events for one discount. Newest at the top
 * (matches the order the backend emits). Each entry shows:
 *   - an icon keyed off the event type (activate / deactivate / enrich / etc.)
 *   - the backend-generated description (already in human-readable form)
 *   - actor + relative timestamp
 *
 * Events come from `selectedDiscount.auditEvents` after the API client UTC-parses
 * their `occurredAt`. Description is precomputed server-side via
 * `generateEventDescription`.
 */

const ICON_BY_EVENT = {
  DiscountActivated: {
    name: "fa-circle-check",
    color: "var(--sbt-success, #3a9b5b)",
  },
  DiscountDeactivated: {
    name: "fa-circle-pause",
    color: "var(--sbt-warning, #d9a648)",
  },
  DiscountEnriched: {
    name: "fa-circle-plus",
    color: "var(--sbt-primary, #5945ff)",
  },
};
const DEFAULT_ICON = { name: "fa-circle", color: "var(--sbt-gray-50, #999)" };

const eventName = (eventType) => (eventType || "").split(":")[1] || eventType;

const iconForEvent = (eventType) =>
  ICON_BY_EVENT[eventName(eventType)] || DEFAULT_ICON;

const formatTimestamp = (date) => {
  if (!date) return "";
  const m = moment(date);
  return `${m.format("MMM D, YYYY h:mm a")} (${m.fromNow()})`;
};

export const PPTDiscountAuditTimeline = ({ events }) => {
  if (!Array.isArray(events) || events.length === 0) {
    return (
      <div className={styles.empty}>
        No audit events recorded for this discount yet.
      </div>
    );
  }

  return (
    <ul className={styles.timeline}>
      {events.map((e) => {
        const icon = iconForEvent(e.eventType);
        return (
          <li key={e.id} className={styles.item}>
            <span className={styles.iconCell} style={{ color: icon.color }}>
              <Icon
                iconType="fa"
                faPackage="fas"
                iconName={icon.name}
                size="small"
              />
            </span>
            <div className={styles.content}>
              <div className={styles.description}>{e.description}</div>
              <div className={styles.meta}>
                <span className={styles.actor}>{e.actorDisplayName}</span>
                <span className={styles.dot}>·</span>
                <span
                  className={styles.timestamp}
                  title={
                    e.occurredAt ? new Date(e.occurredAt).toISOString() : ""
                  }
                >
                  {formatTimestamp(e.occurredAt)}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
